import { useQueryClient } from '@tanstack/react-query';
import { createContext, use, useEffect, useRef, useState, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthProvider';
import {
  VirtualMatrixProvider,
  useVmDispatch,
  type VmOpenLayoutCommand,
} from '../virtualmatrix/useVirtualMatrix';
import { eventsKey, type VmsEvent } from './api';

/**
 * off: not watching (no permission or signed out); connecting: first attempt;
 * live: authenticated and receiving; reconnecting: lost, retrying.
 */
export type LiveStatus = 'off' | 'connecting' | 'live' | 'reconnecting';

interface LiveMessage {
  type: 'ready' | 'event' | 'vm.open_layout';
  vm?: VmOpenLayoutCommand;
  op?: 'created' | 'acknowledged';
  event?: VmsEvent;
}

const LiveContext = createContext<LiveStatus>('off');

/** Server close codes (ADR-016). */
const CLOSE_FORBIDDEN = 4003;
const MAX_BACKOFF_MS = 30_000;
/** Coalesces bursts of events into one refetch. */
const REFRESH_DELAY_MS = 400;

function liveEventsURL(): string {
  const loc = window.location;
  return `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}/api/v1/events/live`;
}

/**
 * Keeps one live event socket per tab (ADR-016). The access token goes in the
 * first message and again on every renewal; lost connections are retried with
 * backoff. Each update refreshes the event queries, so pages and the pending
 * badge stay current without polling, and a reconnect catches up on anything
 * missed while offline.
 */
export function LiveEventsProvider({ children }: { children: ReactNode }) {
  return (
    <VirtualMatrixProvider>
      <LiveEventsSocket>{children}</LiveEventsSocket>
    </VirtualMatrixProvider>
  );
}

function LiveEventsSocket({ children }: { children: ReactNode }) {
  const vmDispatch = useVmDispatch();
  const { status, can, session, accessToken } = useAuth();
  const qc = useQueryClient();
  const enabled = status === 'authenticated' && (can('events.view') || can('monitoring.view'));
  const canEvents = can('events.view');
  const [live, setLive] = useState<Exclude<LiveStatus, 'off'> | 'forbidden'>('connecting');
  const socket = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let backoff = 1000;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let refresh: ReturnType<typeof setTimeout> | undefined;

    const refetch = () => {
      if (refresh) return;
      refresh = setTimeout(() => {
        refresh = undefined;
        void qc.invalidateQueries({ queryKey: eventsKey });
      }, REFRESH_DELAY_MS);
    };

    const connect = () => {
      const ws = new WebSocket(liveEventsURL());
      socket.current = ws;
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token: accessToken() ?? '' }));
      };
      ws.onmessage = (e: MessageEvent) => {
        let msg: LiveMessage;
        try {
          msg = JSON.parse(String(e.data)) as LiveMessage;
        } catch {
          return;
        }
        if (msg.type === 'ready') {
          backoff = 1000;
          setLive('live');
          if (canEvents) refetch(); // catch up on what happened while disconnected
        } else if (msg.type === 'vm.open_layout') {
          if (msg.vm) vmDispatch(msg.vm);
        } else if (canEvents) {
          refetch();
        }
      };
      ws.onclose = (e: CloseEvent) => {
        if (socket.current === ws) socket.current = null;
        if (stopped) return;
        if (e.code === CLOSE_FORBIDDEN) {
          setLive('forbidden');
          return;
        }
        setLive('reconnecting');
        retry = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      };
    };
    connect();

    return () => {
      stopped = true;
      setLive('connecting'); // the next session starts over
      clearTimeout(retry);
      clearTimeout(refresh);
      socket.current?.close(1000);
      socket.current = null;
    };
  }, [enabled, canEvents, qc, accessToken, vmDispatch]);

  // A renewed access token re-authenticates the open socket.
  const token = session?.access_token;
  useEffect(() => {
    const ws = socket.current;
    if (token && ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'auth', token }));
    }
  }, [token]);

  const value: LiveStatus = !enabled || live === 'forbidden' ? 'off' : live;
  return <LiveContext value={value}>{children}</LiveContext>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLiveStatus(): LiveStatus {
  return use(LiveContext);
}
